// Re-export all types from their respective modules
export interface User {
  id: string;
  email: string;
  username: string | null;
  avatar_url: string | null;
  created_at: string;
}

export interface Profile {
  id: string;
  user_id: string;
  username: string;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  followers_count: number;
  following_count: number;
  is_following: boolean;
  age_verified?: boolean;
}

export interface Follow {
  id: string;
  follower_id: string;
  following_id: string;
  created_at: string;
}

export interface SnapMark {
  id: string;
  snap_id: string;
  user_id: string;
  created_at: string;
  created_by: string;
  is_auto: boolean;
}

export interface Snap {
  id: string;
  creator_id: string;
  title: string;
  is_anonymous: boolean;
  is_private: boolean;
  duration_days: number;
  is_influencer: boolean;
  is_trending: boolean;
  created_at: string;
  ends_at: string;
  total_votes: number;
  categories: SnapCategory[];
  view_count: number;
  likes_count: number;
  description: string;
  creator?: User;
  options?: SnapOption[];
  votes?: Vote[];
  comments?: Comment[];
  commentCount?: number;
  isOwner?: boolean;
  userVote?: string;
  userLiked?: boolean;
  isMarked?: boolean;
  marks?: SnapMark[];
  is_system?: boolean;
  cashsnap?: boolean;
  card_image_url?: string;
  winning_option_id?: string;
  prize_distributed?: boolean;
}

export type SnapCategory = 
  | 'politics'
  | 'economy'
  | 'finance'
  | 'relationships'
  | 'lifestyle'
  | 'technology'
  | 'health'
  | 'society_ethics'
  | 'entertainment'
  | 'personal_growth';

export const SNAP_CATEGORIES: SnapCategory[] = [
  'politics',
  'economy',
  'finance',
  'relationships',
  'lifestyle',
  'technology',
  'health',
  'society_ethics',
  'entertainment',
  'personal_growth'
];

export interface SnapOption {
  id: string;
  snap_id: string;
  title: string;
  description: string;
  image_url: string;
  vote_count: number;
  created_at: string;
}

export interface Vote {
  id: string;
  snap_id: string;
  user_id: string;
  option_id: string;
  created_at: string;
}

export type CommentCategory = 'emotional' | 'rational' | 'economic' | 'ethical';
export type ReactionType = 'like' | 'dislike';

export interface Comment {
  id: string;
  snap_id: string;
  user_id: string;
  content: string;
  category: CommentCategory;
  likes_count: number;
  dislikes_count: number;
  created_at: string;
  user?: {
    id: string;
    username: string;
    avatar_url: string | null;
    created_at: string;
  };
  userReaction?: 'like' | 'dislike' | null;
}

export interface CommentReaction {
  id: string;
  comment_id: string;
  user_id: string;
  reaction: ReactionType;
  created_at: string;
}

export interface UserStats {
  snapsCreated: number;
  votesCast: number;
  commentsMade: number;
  rank: 'Novice' | 'Beginner' | 'Intermediate' | 'Advanced' | 'Master' | 'Expert';
  activityScore: number;
}

export type ActivityType = 'snap_created' | 'vote_cast' | 'comment_added' | 'reaction_received';

export interface UserActivity {
  type: ActivityType;
  id: string;
  title: string;
  created_at: string;
  snap_id: string;
  content?: string;
}

export interface AiOpinionRequest {
  question: string;
  questionDetails: string;
  categories: SnapCategory[];
  requestedFeedbackMode: SnapCategory[];
}

export interface AiOpinionResponse {
  output: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface SnapInvite {
  id: string;
  snap_id: string;
  invited_user_id: string;
  inviter_id: string;
  status: 'pending' | 'accepted' | 'declined';
  message?: string;
  created_at: string;
  updated_at: string;
}

export type SnapActivity = 'hot' | 'trending' | 'new' | null;

export interface UserCredits {
  balance: number;
  lifetime_earned: number;
  lifetime_withdrawn: number;
  eligible_for_cashout: boolean;
  min_cashout_amount: number;
}

export interface CreditTransaction {
  id: string;
  snap_id: string | null;
  snap_title: string | null;
  amount: number;
  type: 'earning' | 'withdrawal';
  status: 'pending' | 'completed' | 'failed';
  description: string | null;
  created_at: string;
  processed_at: string | null;
}